/*  MUI  */
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Skeleton,
    Typography
} from '@mui/material';

/*  Shared  */
import { TooltipQ } from 'application/Shared/Components/Tooltips';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';

const RenderSubtitle = ({ subtitle }: { subtitle: string }) => (
    <Typography component="span" sx={{ color: 'text.primaryFaded' }}>
        {subtitle}
    </Typography>
);

/**
 * A card component for displaying data visualizations with customizable header and content
 * @component DataVisualCard
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Content to be rendered inside the card
 * @param {boolean} [props.filteredResults] - Whether to show filtered results message at bottom
 * @param {React.ReactNode} [props.icon] - Optional icon to display in header
 * @param {boolean} [props.loading] - Whether to show loading skeleton
 * @param {string} [props.subtitle] - Optional subtitle text
 * @param {string} props.title - Title text for the card header
 * @param {string} [props.tooltip] - Optional tooltip text key
 * @returns {JSX.Element} DataVisualCard component
 */
export const DataVisualCard = ({
    children,
    filteredResults,
    icon,
    loading,
    subtitle,
    title,
    tooltip
}: {
    children: React.ReactNode;
    filteredResults?: boolean;
    icon?: React.ReactNode;
    loading?: boolean;
    subtitle?: string;
    title: string;
    tooltip?: string;
}): JSX.Element => {
    const { t } = useTranslation();
    return (
        <Card
            variant="outlined"
            data-testid="dataVisualCardCard"
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: '250px'
            }}
        >
            <CardHeader
                title={
                    <Box
                        display="flex"
                        alignItems="center"
                        data-testid="dataVisualCardCardContentTitleIcon"
                    >
                        <Box flex="1">
                            <Box>{title}</Box>
                            {subtitle && <RenderSubtitle subtitle={subtitle} />}
                        </Box>
                        {!!icon && <Box ml="4px">{icon}</Box>}
                        {tooltip && (
                            <TooltipQ text={t(`Tooltips.${tooltip}`)} />
                        )}
                    </Box>
                }
                data-testid="dataVisualCardCardHeader"
            />
            <CardContent
                data-testid="dataVisualCardCardContent"
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    justifyContent: 'center',
                    paddingBottom: '16px',
                    paddingTop: 0
                }}
            >
                {loading ? (
                    <Box style={{ width: '100%' }}>
                        <Skeleton
                            animation="wave"
                            style={{
                                minHeight: '100px',
                                textAlign: 'center'
                            }}
                            variant="rectangular"
                        />
                    </Box>
                ) : (
                    <Box
                        data-testid="dataVisualCardChildren"
                        width="100%"
                        height="100%"
                        display="flex"
                    >
                        {children}
                    </Box>
                )}

                {filteredResults && (
                    <Box alignSelf="flex-end">
                        <Typography>
                            <small>*{t('Common.filtered_results')}</small>
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};
